import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  NativeDateModule
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';

import { PolicyAddVehicleDataAccessModule } from '@amfam/policy/add-vehicle/data-access';
import { PolicyAddVehicleUiModule } from '@amfam/policy/add-vehicle/ui';
import { PolicyPipesModule } from '@amfam/policy/pipes';
import { PolicySharedModule } from '@amfam/policy/shared';
import { PolicyVehicleCoveragesDataAccessModule } from '@amfam/policy/vehicle-coverages/data-access';
import { MY_NATIVE_DATE_FORMATS } from '@amfam/shared/models';
import { DsRouterLinkModule } from '@amfam/shared/ui/ds-router-link';
import { PipesModule } from '@amfam/shared/ui/pipes';
import { RiskListModule } from '@amfam/shared/ui/risk-list';
import { BrandDirectivesModule } from '@amfam/shared/utility/brand';
import { ImpersonationModule } from '@amfam/shared/utility/impersonation';
import {
  ENVIRONMENT,
  EnvironmentBase,
  SharedServicesModule
} from '@amfam/shared/utility/shared-services';
import { UiKitModule } from '@amfam/ui-kit';

import { AddNewVehicleFeaturesEffects } from './+state/add-vehicle-feature.effects';
import * as fromAddVehicleReducer from './+state/add-vehicle-feature.reducer';
import { AddVehicleContainerComponent } from './add-vehicle-container/add-vehicle-container.component';
import { AfiVehicleQuoteSummaryComponent } from './afi-vehicle-quote-summary/afi-vehicle-quote-summary.component';
import { AddVehicleRoutes } from './routes/add-vehicle.routes';
import { UwQuestionsComponent } from './uw-questions/uw-questions.component';
import { VehicleAgentNotificationConfirmationComponent } from './vehicle-agent-notification-confirmation/vehicle-agent-notification-confirmation.component';
import { VehicleDriverInfoComponent } from './vehicle-driver-info/vehicle-driver-info.component';
import { VehicleDriverSelectionComponent } from './vehicle-driver-selection/vehicle-driver-selection.component';
import { VehicleExpectationSettingComponent } from './vehicle-expectation-setting/vehicle-expectation-setting.component';
import { VehicleFeatureEquipmentComponent } from './vehicle-feature-equipment/vehicle-feature-equipment.component';
import { VehicleFuturePaymentScheduleComponent } from './vehicle-future-payment-schedule/vehicle-future-payment-schedule.component';
import { VehicleGarageAddressComponent } from './vehicle-garage-address/vehicle-garage-address.component';
import { VehicleLoanInfoComponent } from './vehicle-loan-info/vehicle-loan-info.component';
import { VehicleOfflineBindComponent } from './vehicle-offline-bind/vehicle-offline-bind.component';
import { VehiclePurchaseInfoComponent } from './vehicle-purchase-info/vehicle-purchase-info.component';
import { VehicleQuoteInfoComponent } from './vehicle-quote-info/vehicle-quote-info.component';
import { VehicleQuoteSummmaryComponent } from './vehicle-quote-summmary/vehicle-quote-summmary.component';
import { VehicleSecondaryDriverSelectionComponent } from './vehicle-secondary-driver-selection/vehicle-secondary-driver-selection.component';
import { VehicleUnderwritingQuestionsComponent } from './vehicle-underwriting-questions/vehicle-underwriting-questions.component';
import { VehicleUseMileageComponent } from './vehicle-use-mileage/vehicle-use-mileage.component';
import { VehicleVinOdoComponent } from './vehicle-vin-odo/vehicle-vin-odo.component';

export function getEnvironment() {
  return PolicyAddVehicleFeatureModule.environment;
}

@NgModule({
  imports: [
    CommonModule,
    UiKitModule,
    SharedServicesModule,
    PipesModule,
    FormsModule,
    ReactiveFormsModule,
    NgxDatatableModule,
    ImpersonationModule,
    DsRouterLinkModule,
    BrandDirectivesModule,
    PolicyPipesModule,
    RiskListModule,
    PolicyAddVehicleUiModule,
    PolicyAddVehicleDataAccessModule,
    RouterModule.forChild(AddVehicleRoutes),
    StoreModule.forFeature(
      fromAddVehicleReducer.ADD_VEHICLE_FEATURE_KEY,
      fromAddVehicleReducer.reducer
    ),
    EffectsModule.forFeature([AddNewVehicleFeaturesEffects]),
    PolicyVehicleCoveragesDataAccessModule,
    PolicySharedModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatDatepickerModule,
    NativeDateModule
  ],
  declarations: [
    VehicleVinOdoComponent,
    AddVehicleContainerComponent,
    VehiclePurchaseInfoComponent,
    VehicleOfflineBindComponent,
    VehicleAgentNotificationConfirmationComponent,
    VehicleUseMileageComponent,
    VehicleGarageAddressComponent,
    VehicleFeatureEquipmentComponent,
    VehicleUnderwritingQuestionsComponent,
    VehicleLoanInfoComponent,
    VehicleDriverInfoComponent,
    VehicleDriverSelectionComponent,
    VehicleSecondaryDriverSelectionComponent,
    VehicleQuoteInfoComponent,
    VehicleQuoteSummmaryComponent,
    UwQuestionsComponent,
    VehicleExpectationSettingComponent,
    AfiVehicleQuoteSummaryComponent,
    VehicleFuturePaymentScheduleComponent
  ],
  providers: [
    {
      provide: ENVIRONMENT,
      useFactory: getEnvironment
    },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_NATIVE_DATE_FORMATS }
  ]
})
export class PolicyAddVehicleFeatureModule {
  static environment: EnvironmentBase;
}
